import { User } from 'services/requests';
import {
  useInviteIndependentIraUser,
  useResendInviteIndependentIraUser,
} from 'hooks/api/independentIra';
import { checkUser } from 'utils';
import Button from 'components/Button';
import { Modal, ModalProps } from 'components/Modal';
import { showInfoToast } from 'components/Toast';

import messages from './ConfirmInviteModal.messages';
import styles from './ConfirmInviteModal.module.css';

type Props<T> = {
  onRequestClose: VoidFunction;
  user?: T;
  isResend: boolean;
} & ModalProps;

// NOTE: Only for Independent IRA users
const ConfirmInviteModal = <T extends User>({
  onRequestClose,
  user,
  isResend,
  ...restOfProps
}: Props<T>): JSX.Element | null => {
  const {
    mutateAsync: inviteIndependentIraUser,
    isLoading: inviteIndependentIraUserLoading,
  } = useInviteIndependentIraUser();
  const {
    mutateAsync: resendInviteIndependentIraUser,
    isLoading: resendInnviteIndependentIraUserLoading,
  } = useResendInviteIndependentIraUser();

  const isLoading =
    inviteIndependentIraUserLoading || resendInnviteIndependentIraUserLoading;

  const legalName =
    user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '';

  const hasUserManager = !!user?.userManagers?.length;

  const getTextStart = () => {
    if (hasUserManager) {
      return messages.sendEmailToUserManagerStart;
    }
    return messages.moveToInvitedTextStart;
  };

  const getTextEnd = () => {
    if (hasUserManager) {
      return messages.sendEmailToUserManagerEnd;
    }
    return messages.moveToInvitedTextEnd;
  };

  const getSubmitButtonText = () => {
    if (hasUserManager) {
      return messages.notifyButton;
    }
    return messages.moveToInvitedButton;
  };

  const onSubmit = async () => {
    if (checkUser(user)) {
      if (isResend) {
        const resendResult = await resendInviteIndependentIraUser(user.id.toString());
        if (resendResult.success) {
          if (hasUserManager) {
            showInfoToast(messages.successEmailToUserManager);
          } else {
            showInfoToast(messages.successMoveToInvited);
          }
        }
      } else {
        const inviteResult = await inviteIndependentIraUser(user.id.toString());
        if (inviteResult.success) {
          if (hasUserManager) {
            showInfoToast(messages.successEmailToUserManager);
          } else {
            showInfoToast(messages.successMoveToInvited);
          }
        }
      }
      onRequestClose();
    }
  };

  return (
    <Modal
      {...restOfProps}
      onRequestClose={onRequestClose}
      className={styles.confirmInviteModal}
      disabled={isLoading}
    >
      <h3 className={styles.confirmInviteModal__title}>{messages.title}</h3>
      <p className={styles.confirmInviteModal__text}>
        {getTextStart()}
        {hasUserManager ? `${user?.userManagers?.[0]?.userName} that ` : ``}
        {legalName}
        {getTextEnd()}
      </p>

      <div className={styles.confirmInviteModal__buttons}>
        <Button variant={'outline'} onClick={onRequestClose} disabled={isLoading}>
          {messages.buttonCancel}
        </Button>

        <Button
          onClick={onSubmit}
          className={
            !hasUserManager
              ? styles.confirmInviteModal__moveToInvitedButton
              : styles.confirmInviteModal__submitButton
          }
          isLoading={isLoading}
        >
          {getSubmitButtonText()}
        </Button>
      </div>
    </Modal>
  );
};

export default ConfirmInviteModal;
